const STOCKS_BASE_URL = "https://www.incredmoney.com/stocks";
const INDICES_BASE_URL = "https://www.incredmoney.com/indices";

/** Display names whose URL slug does not match the default name → kebab mapping. */
const INDEX_SLUG_OVERRIDES: Record<string, string> = {
    "BSE Sensex": "sensex",
    "Nifty Oil & Gas": "nifty-oil-gas",
};

/**
 * Footer “Top Stocks” label (without “Share Price”) → incredmoney.com stock slug.
 * Prefer exact slugs from the known stock link catalog; only derive when absent.
 */
const TOP_STOCK_SLUG_BY_LABEL: Record<string, string> = {
    "Ashok Leyland": "ashok-leyland-ltd",
    CDSL: "central-depository-services-india-ltd",
    // Not in catalog — follows Adani * Ltd pattern (adani-enterprises-ltd, adani-power-ltd)
    "Adani Wilmar": "adani-wilmar-ltd",
    "Canara Bank": "canara-bank",
    "COAL India": "coal-india-ltd",
    "Adani Ports": "adani-ports-and-special-economic-zone-ltd",
    "HDFC Bank": "hdfc-bank-ltd",
    "Eicher Motors": "eicher-motors-ltd",
    "Bajaj Finance": "bajaj-finance-ltd",
    "Tata Consultancy Services": "tata-consultancy-services-ltd",
    HFCL: "hfcl-ltd",
    Dmart: "avenue-supermarts-ltd",
    "HCL Technologies": "hcl-technologies-ltd",
    HUL: "hindustan-unilever-ltd",
    Infosys: "infosys-ltd",
    "Eternal (Zomato)": "eternal-ltd",
    "ICICI Bank": "icici-bank-ltd",
    ITC: "itc-ltd",
    "JSW Steel": "jsw-steel-ltd",
    "Hindalco Industries": "hindalco-industries-ltd",
    IRCTC: "indian-railway-catering-and-tourism-corporation-ltd",
    NTPC: "ntpc-ltd",
    "Larsen & Toubro": "larsen-and-toubro-ltd",
    "Power Grid Corporation": "power-grid-corporation-of-india-ltd",
    Cipla: "cipla-ltd",
    "Reliance Industries": "reliance-industries-ltd",
    SBI: "state-bank-of-india",
    "Axis Bank": "axis-bank-ltd",
    "Maruti Suzuki": "maruti-suzuki-india-ltd",
    "Tata Motors": "tata-motors-ltd",
    Wipro: "wipro-ltd",
    "Bharti Airtel": "bharti-airtel-ltd",
    "Sun Pharma": "sun-pharmaceutical-industries-ltd",
    "Asian Paints": "asian-paints-ltd",
    Titan: "titan-company-ltd",
    "UltraTech Cement": "ultratech-cement-ltd",
    "Nestle India": "nestle-india-ltd",
    "Mahindra & Mahindra": "mahindra-and-mahindra-ltd",
    "Tech Mahindra": "tech-mahindra-ltd",
    "IndusInd Bank": "indusind-bank-ltd",
    "Bajaj Auto": "bajaj-auto-ltd",
    "Dr Reddy's": "dr-reddys-laboratories-ltd",
    "Adani Enterprises": "adani-enterprises-ltd",
    ONGC: "oil-and-natural-gas-corpn-ltd",
    BPCL: "bharat-petroleum-corporation-ltd",
    "Tata Steel": "tata-steel-ltd",
    Vedanta: "vedanta-ltd",
    Pidilite: "pidilite-industries-ltd",
    "Godrej Consumer": "godrej-consumer-products-ltd",
    "Divi's Lab": "divis-laboratories-ltd",
};

/**
 * Converts a market display name into the incredmoney.com URL slug.
 * "Tata Power Company Ltd" → "tata-power-company-ltd"
 * "Nifty 50" → "nifty-50"
 */
export function toStockSlug(name: string): string {
    return name
        .toLowerCase()
        .trim()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
}

/** Strips designed line breaks and the “Share Price” suffix from footer labels. */
export function normalizeTopStockLabel(label: string): string {
    return label
        .replace(/\n/g, " ")
        .replace(/\s+Share Price\s*$/i, "")
        .replace(/\s+/g, " ")
        .trim();
}

/** "Reliance Industries Ltd" → "https://www.incredmoney.com/stocks/reliance-industries-ltd" */
export function getStockPageUrl(name: string): string {
    return `${STOCKS_BASE_URL}/${toStockSlug(name)}`;
}

/**
 * Footer Top Stocks destination.
 * Uses the centralized Top Stocks slug map when present; otherwise falls back to
 * `getStockPageUrl` with an “Ltd” suffix (project convention for equity pages).
 */
export function getTopStockPageUrl(footerLabel: string): string {
    const key = normalizeTopStockLabel(footerLabel);
    const slug = TOP_STOCK_SLUG_BY_LABEL[key];
    if (slug) {
        return `${STOCKS_BASE_URL}/${slug}`;
    }
    return getStockPageUrl(/ltd$/i.test(key) ? key : `${key} Ltd`);
}

/** "Nifty Bank" → "https://www.incredmoney.com/indices/nifty-bank" */
export function getIndexPageUrl(name: string): string {
    const slug = INDEX_SLUG_OVERRIDES[name] ?? toStockSlug(name);
    return `${INDICES_BASE_URL}/${slug}`;
}

/** Hub page for all indices: https://www.incredmoney.com/indices */
export function getIndicesListUrl(): string {
    return INDICES_BASE_URL;
}
